import React, { useRef, useState } from 'react';
import { NumberFieldProps } from '../../../types/DesignSystem/NumberField';
import FieldTitle from '../FieldTitle/FieldTitle';
import { FieldMessages, fieldInvalid, messageIds } from '../fieldHelper';
import { useControllableState } from '../useControllableState';
import { Add, Minus } from '../Iconography/Icons';
import { useMergedRef } from '../useMergedRef';
import { CONTROL_HEIGHT, FIELD_INSET } from '../controlSize';
import { FIELD_BOX, fieldBorder, FIELD_REST_WITHIN, FIELD_DISABLED } from '../fieldChrome';
import { useControlSize } from '../DensityProvider/DensityProvider';
import { isNumberDraft, localeSeparators, parseNumberDraft } from './numberDraft';

/**
 * NumberField (§ 305) — see the type file for why this is a text input wearing
 * `role="spinbutton"` rather than `<input type="number">`.
 *
 * **The border lives on the wrapper**, the Combobox / Search arrangement (§ 29): the
 * input, the unit and the two stepper buttons share one bordered row, so the focus
 * treatment is `focus-within`. The steppers are mouse affordances outside the tab
 * order — the input already owns the keyboard (arrows step, Shift ×10, Home / End to
 * the bounds), and a field with three tab stops is a field nobody can tab past.
 *
 * **Typing is permissive, committing is strict.** While the user types, anything that
 * could still become a number is allowed — a lone `-`, a trailing `.`, a `,` as the
 * decimal mark — and `onChange` reports every intermediate value that parses. On blur
 * the draft is replaced by the normalised value (clamped, rounded), so the field never
 * shows one thing while holding another.
 */

const decimalsOf = (n: number): number => {
  const text = String(n);
  const dot = text.indexOf('.');
  return dot < 0 ? 0 : text.length - dot - 1;
};

/* § 375 — the draft rule and the parse live in `numberDraft.ts`: a complete grouping in the
   runtime locale ("1,000" in en-US) reads as that number; anything else reads as it always
   did, one mark and that mark the decimal point. `navigator.language` is the locale the
   browser formats in, and jsdom answers it deterministically (en-US). */
const runtimeSeparators = () =>
  localeSeparators(typeof navigator !== 'undefined' ? navigator.language : undefined);

const NumberField = React.forwardRef<HTMLInputElement, NumberFieldProps>(function NumberField({
  label,
  value: valueProp,
  defaultValue,
  onChange,
  min,
  max,
  step = 1,
  precision,
  unit,
  steppers = true,
  placeholder = '',
  disabled = false,
  required = false,
  error = false,
  helperText = '',
  errorMessage,
  hideLabel = false,
  size: sizeProp,
  labelSize,
  className = '',
  id,
  name,
  'data-testid': dataTestId,
  onFocus: onFocusProp,
  onBlur: onBlurProp,
  onKeyDown: onKeyDownProp,
  ...rest
}, forwardedRef) {
  /* § 313 — the region's density decides when the caller did not. */
  const size = useControlSize(sizeProp);
  const uid = React.useId();
  const inputId = id ?? `${uid}-input`;
  const helperId = `${uid}-helper`;
  const errorId = `${uid}-error`;
  /* § 356 / § 357 — see TextField. Every value change goes through `emit`. */
  const invalid = fieldInvalid(error, errorMessage);
  const [value, setValue] = useControllableState<number | null>(valueProp, defaultValue, null);
  const emit = (next: number | null) => {
    setValue(next);
    onChange?.(next);
  };
  const inputRef = useRef<HTMLInputElement>(null);
  const setInputRef = useMergedRef<HTMLInputElement>(inputRef, forwardedRef);

  const decimals = precision ?? decimalsOf(step);
  const format = (n: number | null): string => (n === null ? '' : decimals > 0 ? n.toFixed(decimals) : String(n));
  const normalise = (n: number): number => {
    let next = n;
    if (min !== undefined) next = Math.max(min, next);
    if (max !== undefined) next = Math.min(max, next);
    return Number(next.toFixed(decimals));
  };

  /* The draft is what the user has TYPED and is `null` when nobody is editing, so the
     field shows the normalised value the moment focus leaves. */
  const [draft, setDraft] = useState<string | null>(null);
  const shown = draft ?? format(value);

  const separators = runtimeSeparators();

  const commit = () => {
    const parsed = parseNumberDraft(draft ?? '', separators);
    const next = parsed === null ? null : normalise(parsed);
    if (next !== value) emit(next);
    setDraft(null);
  };

  const stepBy = (multiplier: number) => {
    if (disabled) return;
    const base = value ?? (min !== undefined && min > 0 ? min : 0);
    const next = normalise(base + step * multiplier);
    if (next !== value) emit(next);
    setDraft(draft === null ? null : format(next));
  };

  const atMin = min !== undefined && value !== null && value <= min;
  const atMax = max !== undefined && value !== null && value >= max;

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const raw = event.target.value;
    if (!isNumberDraft(raw, separators)) return;
    if (raw.startsWith('-') && min !== undefined && min >= 0) return;
    setDraft(raw);
    const parsed = parseNumberDraft(raw, separators);
    if (parsed !== value) emit(parsed);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    let handled = true;
    switch (event.key) {
      case 'ArrowUp': stepBy(event.shiftKey ? 10 : 1); break;
      case 'ArrowDown': stepBy(event.shiftKey ? -10 : -1); break;
      case 'PageUp': stepBy(10); break;
      case 'PageDown': stepBy(-10); break;
      case 'Home': if (min !== undefined) { emit(normalise(min)); setDraft(null); } else handled = false; break;
      case 'End': if (max !== undefined) { emit(normalise(max)); setDraft(null); } else handled = false; break;
      case 'Enter': commit(); handled = false; break;
      default: handled = false;
    }
    if (handled) event.preventDefault();
    if (!event.defaultPrevented) onKeyDownProp?.(event);
  };

  const stepperClass = (off: boolean) =>
    `tw-flex tw-h-6 tw-w-6 tw-shrink-0 tw-items-center tw-justify-center tw-rounded-ctl tw-bg-transparent tw-border-0 tw-p-0 tw-transition-colors motion-safe:tw-duration-fast motion-safe:tw-ease-swift ${
      off ? 'tw-text-ink-disabled tw-cursor-not-allowed' : 'tw-text-ink-tertiary hover:tw-text-ink hover:tw-bg-surface-hover-strong'
    }`;

  return (
    <div className={className}>
      {label !== '' && (
        <FieldTitle
          label={label}
          required={required}
          /* § 355 — hidden, not removed: it stays the input's accessible name. */
          className={hideLabel ? 'tw-sr-only' : 'tw-mb-2'}
          size={labelSize}
          disabled={disabled}
          htmlFor={inputId}
        />
      )}

      <div
        data-testid={dataTestId ? `${dataTestId}-field` : undefined}
        className={`tw-flex tw-items-center tw-gap-2 ${CONTROL_HEIGHT[size]} ${FIELD_INSET[size]} ${FIELD_BOX} ${fieldBorder({ error: invalid, disabled })}
          ${disabled ? FIELD_DISABLED : `tw-cursor-text ${FIELD_REST_WITHIN}`}
          ${invalid && !disabled ? 'focus-within:tw-ring-tone-danger-glyph' : ''}
        `}
      >
        <input
          /* rest first — the Button.tsx rule; the handlers this control owns come after. */
          {...rest}
          ref={setInputRef}
          id={inputId}
          name={name}
          type="text"
          inputMode={decimals > 0 ? 'decimal' : 'numeric'}
          role="spinbutton"
          aria-valuenow={value ?? undefined}
          aria-valuemin={min}
          aria-valuemax={max}
          aria-valuetext={value === null ? undefined : unit ? `${format(value)} ${unit}` : undefined}
          aria-required={required || undefined}
          aria-invalid={invalid || undefined}
          aria-describedby={messageIds({ helperId, errorId }, { helperText, errorMessage }).filter(Boolean).join(' ') || undefined}
          value={shown}
          placeholder={placeholder}
          disabled={disabled}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          onFocus={(event) => {
            setDraft(format(value));
            onFocusProp?.(event);
          }}
          onBlur={(event) => {
            commit();
            onBlurProp?.(event);
          }}
          data-testid={dataTestId}
          className={`tw-flex-1 tw-min-w-0 tw-bg-transparent tw-border-0 tw-outline-none tw-p-0 tw-text-body-primary tw-tabular-nums placeholder:tw-text-ink-secondary
            ${disabled ? 'tw-text-ink-disabled tw-cursor-not-allowed' : 'tw-text-ink'}`}
        />
        {unit && (
          <span
            aria-hidden="true"
            data-testid={dataTestId ? `${dataTestId}-unit` : undefined}
            className={`tw-shrink-0 tw-text-caption-metadata tw-select-none ${disabled ? 'tw-text-ink-disabled' : 'tw-text-ink-tertiary'}`}
          >
            {unit}
          </span>
        )}
        {steppers && (
          <div className="tw-flex tw-shrink-0 tw-items-center tw-gap-0.5 -tw-mr-1">
            <button
              type="button"
              tabIndex={-1}
              aria-label="Decrease"
              disabled={disabled || atMin}
              data-testid={dataTestId ? `${dataTestId}-decrement` : undefined}
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => { stepBy(-1); inputRef.current?.focus(); }}
              className={stepperClass(disabled || atMin)}
            >
              <Minus size={14} aria-hidden="true" />
            </button>
            <button
              type="button"
              tabIndex={-1}
              aria-label="Increase"
              disabled={disabled || atMax}
              data-testid={dataTestId ? `${dataTestId}-increment` : undefined}
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => { stepBy(1); inputRef.current?.focus(); }}
              className={stepperClass(disabled || atMax)}
            >
              <Add size={14} aria-hidden="true" />
            </button>
          </div>
        )}
      </div>

      <FieldMessages
        helperId={helperId}
        errorId={errorId}
        helperText={helperText}
        errorMessage={errorMessage}
        error={error}
        className="tw-mt-1"
        data-testid={dataTestId}
      />
    </div>
  );
});

export default NumberField;
